import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { useRegion } from 'common/hooks/region';
import { SettingsPage } from 'layouts/Settings/SettingsPage';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTerminalCredentials, TerminalCredential } from 'services/clients/credentials';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { Tutorial } from 'components/Tutorial/Tutorial';
import {
  TUTORIAL_CREDENTIALS_DESCRIPTION,
  TUTORIAL_CREDENTIALS_DURATION,
  TUTORIAL_CREDENTIALS_SEGMENT_KEY,
  TUTORIAL_CREDENTIALS_TITLE,
} from 'components/Tutorial/TutorialCopy';
import { PageHeader } from 'layouts/Generic/page-header';
import { CredentialsTable } from './components';
import { ActionBar } from './components/action-bar/ActionBar';
import { CREDENTIALS_QUERY_PARAMS } from './components/credentials-table/filtering';
import { UpdateCredential } from './components/update-credential';
import { ActionHandler } from './actions';
import { DeleteCredential } from './components/delete-credential';
import { AddCredential } from './components/add-credential';
import { MissingCredentials } from './components/missing-credentials';
import { QueryKeys } from './queries';

export const TerminalCredentials: React.FC = () => {
  const { searchParams } = useCustomSearchParams(CREDENTIALS_QUERY_PARAMS);
  const region = useRegion();
  const credentialsQuery = useQuery(
    [QueryKeys.TerminalCredentials, region.code, searchParams],
    ({ signal }) =>
      getTerminalCredentials({ ...searchParams, portCollectionCodes: region.code }, signal),
    { keepPreviousData: true }
  );

  const [state, setState] = React.useState<{
    selection: GridSelectionModel;
    addCredential: boolean;
    deleteCredentials: TerminalCredential[];
    editCredential?: TerminalCredential;
  }>({
    selection: [],
    addCredential: false,
    deleteCredentials: [],
    editCredential: undefined,
  });

  const handleAction: ActionHandler = React.useCallback(
    (action, credentials) => {
      if (credentials.length === 0) {
        return;
      }

      switch (action) {
        case 'update':
          if (credentials.length === 1) {
            setState((s) => ({
              ...s,
              editCredential: credentialsQuery.data?.results.find((c) => c.id === credentials[0]),
            }));
          }
          break;
        case 'delete': {
          const deleteCredentials = credentials.map((cId) =>
            credentialsQuery.data?.results.find((c) => c.id === cId)
          ) as TerminalCredential[];
          setState((s) => ({
            ...s,
            deleteCredentials,
          }));
          break;
        }
        default:
          break;
      }
    },
    [setState, credentialsQuery.data]
  );

  return (
    <SettingsPage className="space-y-8 flex flex-col">
      <div className="px-6 space-y-6">
        <Tutorial
          title={TUTORIAL_CREDENTIALS_TITLE}
          description={TUTORIAL_CREDENTIALS_DESCRIPTION}
          tutorialKey="credentials"
          tutorialDuration={TUTORIAL_CREDENTIALS_DURATION}
          segmentKey={TUTORIAL_CREDENTIALS_SEGMENT_KEY}
        />
        <MissingCredentials />
        <PageHeader title="Terminal Credentials for Appointment Systems" />
        <ActionBar
          selection={state.selection}
          onAction={handleAction}
          onAddCredential={() => {
            setState((s) => ({ ...s, addCredential: true }));
          }}
        />
      </div>
      <div className="flex-grow">
        <CredentialsTable
          credentialsQuery={credentialsQuery}
          onAction={handleAction}
          onSelectionChange={(selection) => {
            setState((s) => ({ ...s, selection }));
          }}
          onCreate={(editCredential) => {
            setState((s) => ({ ...s, editCredential }));
          }}
        />
      </div>
      <UpdateCredential
        credential={state.editCredential}
        onClose={() => setState((s) => ({ ...s, editCredential: undefined }))}
      />
      <DeleteCredential
        credentials={state.deleteCredentials}
        onClose={() => setState((s) => ({ ...s, deleteCredentials: [] }))}
      />
      <AddCredential
        open={state.addCredential}
        onClose={() => setState((s) => ({ ...s, addCredential: false }))}
      />
    </SettingsPage>
  );
};
TerminalCredentials.displayName = 'TerminalCredentials';
